import React from 'react';
import PropTypes from 'prop-types';
import { UniversalLink } from '@plone/volto/components';
import { ICONS } from './icons';
import CabecalhoSecao from '../../components/CabecalhoSecao';
import saudeIndigenaImage from '../../theme/work-with-us/saude-indigena.jpeg';
import sedeAdministrativaImage from '../../theme/work-with-us/sede-administrativa.jpeg';

const OPPORTUNITY_LABELS = new Set(['PS', 'SI', 'VN', 'RD']);
const LEGACY_OPPORTUNITY_IMAGES = {
  SI: saudeIndigenaImage,
  VN: sedeAdministrativaImage,
};

const OpportunityCard = ({ item }) => {
  const label = (item.iconLabel || '').toUpperCase();
  const image = item.imageUrl || LEGACY_OPPORTUNITY_IMAGES[label];

  return (
    <article className="ag-opportunity-card">
      <div className="ag-opportunity-card__media">
        {image ? (
          <img src={image} alt="" loading="lazy" />
        ) : (
          <div className="ag-opportunity-card__placeholder">
            <span aria-hidden="true">{label || 'Ag'}</span>
            <strong>{item.title}</strong>
          </div>
        )}
      </div>
      <div className="ag-opportunity-card__body">
        {image && <h3>{item.title}</h3>}
        {item.body && <p>{item.body}</p>}
        {item.href && (
          <UniversalLink
            href={item.href}
            className="ag-opportunity-card__action"
            aria-label={
              item.title
                ? `${item.buttonLabel || 'Acessar'}: ${item.title}`
                : undefined
            }
          >
            {item.buttonLabel || 'Acessar'}
          </UniversalLink>
        )}
      </div>
    </article>
  );
};

OpportunityCard.propTypes = {
  item: PropTypes.object.isRequired,
};

// Fundo da seção (22/10/2026): "creme" põe o bloco numa faixa de ponta a ponta,
// com o mesmo miolo do cabeçalho de página; sem o campo, o HTML fica igual.
const comFundo = (fundo, conteudo) =>
  fundo === 'creme' ? (
    <div className="ag-cards-fundo ag-cards-fundo--creme">
      <div className="ag-cards-fundo__inner">{conteudo}</div>
    </div>
  ) : (
    conteudo
  );

const CardsView = ({ data }) => {
  const items = data.items || [];
  const cols = data.columns || '3';
  // Conteudos legados anteriores a criacao das variacoes nao possuem o campo
  // `variant`. Nesse caso, a propria estrutura editorial do bloco determina a
  // apresentacao: uma colecao composta apenas por links, sem textos de apoio,
  // e uma lista de acesso — nao um conjunto de cards promocionais.
  //
  // Isso mantem a decisao dentro do bloco, funciona apos mover/renomear a
  // pagina e nao exige migracao ou alteracao do conteudo no Plone.
  const hasOnlyLinkItems =
    items.length > 0 &&
    items.every(
      (item) =>
        Boolean(item.href) &&
        (typeof item.body !== 'string' || item.body.trim() === ''),
    );
  const hasOnlyOpportunityItems =
    items.length > 0 &&
    items.every((item) =>
      OPPORTUNITY_LABELS.has((item.iconLabel || '').toUpperCase()),
    );
  const variant =
    data.variant ||
    (hasOnlyLinkItems
      ? 'links'
      : hasOnlyOpportunityItems
        ? 'opportunities'
        : 'cards');

  if (variant === 'links') {
    // A busca de consultas públicas era pendurada aqui por gatilho escondido
    // (todos os cards com selo CP); virou o bloco agsusConsultationSearch.
    // Selo, título e colunas vêm do mesmo formulário das outras variações:
    // antes a lista os ignorava (o título só nomeava a seção para o leitor de
    // tela e a grade tinha 2 colunas fixas). Sem título, a seção fica sem nome
    // e não vira região: várias regiões chamadas só "Links" não se distinguem
    // para o leitor de tela, que chega à lista pelo título de texto acima dela.
    return comFundo(
      data.fundo,
      <section
        className="ag-links-section"
        aria-label={data.title || undefined}
      >
        <CabecalhoSecao
          prefixo="ag-cards-section"
          selo={data.eyebrow}
          titulo={data.title}
          linkTexto={data.linkText}
          linkHref={data.linkHref}
        />
        <div
          className="agsus-transparency__links"
          style={{ '--ag-links-cols': cols }}
        >
          {items.map((item, idx) => (
            <UniversalLink
              key={item.href || item.title || idx}
              href={item.href || '#'}
            >
              <span>{item.title}</span>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M5 12h14m-5-5 5 5-5 5" />
              </svg>
            </UniversalLink>
          ))}
        </div>
      </section>,
    );
  }

  if (variant === 'opportunities') {
    return comFundo(
      data.fundo,
      <>
        <section className="ag-opportunities-section">
          <CabecalhoSecao
            prefixo="ag-cards-section"
            selo={data.eyebrow}
            titulo={data.title}
            linkTexto={data.linkText}
            linkHref={data.linkHref}
          />
          <div
            className="ag-opportunities-grid"
            style={{ '--ag-opportunity-cols': cols }}
          >
            {items.map((item, idx) => (
              <OpportunityCard
                key={item['@id'] || item.href || item.title || idx}
                item={item}
              />
            ))}
          </div>
        </section>
      </>,
    );
  }
  return comFundo(
    data.fundo,
    <section className="ag-cards-section">
      <CabecalhoSecao
        prefixo="ag-cards-section"
        selo={data.eyebrow}
        titulo={data.title}
        linkTexto={data.linkText}
        linkHref={data.linkHref}
      />
      <div className="ag-cards-grid" style={{ '--ag-cols': cols }}>
        {items.map((item, idx) => {
          const iconSvg = item.icon ? ICONS[item.icon] : null;
          const Inner = (
            <>
              <div className="ag-card-icon__circle" aria-hidden="true">
                {iconSvg || (
                  <span>
                    {(item.iconLabel || '•').slice(0, 2).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="ag-card-icon__body">
                <h3 className="ag-card-icon__title">{item.title}</h3>
                {item.body && <p>{item.body}</p>}
                {item.href && (
                  <span className="ag-card-icon__cta">
                    {item.buttonLabel || 'Acessar'} →
                  </span>
                )}
              </div>
            </>
          );
          return item.href ? (
            <UniversalLink key={idx} href={item.href} className="ag-card-icon">
              {Inner}
            </UniversalLink>
          ) : (
            <div key={idx} className="ag-card-icon">
              {Inner}
            </div>
          );
        })}
      </div>
    </section>,
  );
};

CardsView.propTypes = {
  data: PropTypes.shape({
    eyebrow: PropTypes.string,
    title: PropTypes.string,
    columns: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    fundo: PropTypes.string,
    variant: PropTypes.string,
    items: PropTypes.array,
  }).isRequired,
};

export default CardsView;
